import {
	PAYMENT_TYPE,
	PAYMENT_STATUS,
	LOCKING_BANK_TRANSFER_REQUEST_STATUSES,
	PAY_LATER_CURRENCIES,
	BANK_TRANSFER_REQUEST_KIND,
} from '@arbitrip/constants';
import { RESERVATION_STATUS } from '@arbitrip/constants/reservations';

import { getBankTransferDeadline, isBankTransferAvailable } from '@/utils/PayLater';
import { isAccountBankTransferMode, getSettlementKind } from '@/utils/AccountBankTransfer';

export const SELECTION_INELIGIBILITY_REASON = {
	NOT_PAY_LATER: 'NOT_PAY_LATER',
	ALREADY_PAID: 'ALREADY_PAID',
	OPEN_BANK_TRANSFER_REQUEST: 'OPEN_BANK_TRANSFER_REQUEST',
	PAST_DEADLINE: 'PAST_DEADLINE',
	NOT_APPROVED: 'NOT_APPROVED',
	NO_PAYMENT: 'NO_PAYMENT',
	NOT_PAY_LATER_OR_ACCOUNT: 'NOT_PAY_LATER_OR_ACCOUNT',
	UNSUPPORTED_CURRENCY: 'UNSUPPORTED_CURRENCY',
};

const ELIGIBILITY_TOOLTIP_TRANSLATION_KEY = {
	[SELECTION_INELIGIBILITY_REASON.NOT_PAY_LATER]: 'Bookings.SelectToPay.Ineligible.NotPayLater',
	[SELECTION_INELIGIBILITY_REASON.ALREADY_PAID]: 'Bookings.SelectToPay.Ineligible.AlreadyPaid',
	[SELECTION_INELIGIBILITY_REASON.OPEN_BANK_TRANSFER_REQUEST]: 'Bookings.SelectToPay.Ineligible.OpenBankTransferRequest',
	[SELECTION_INELIGIBILITY_REASON.PAST_DEADLINE]: 'Bookings.SelectToPay.Ineligible.PastDeadline',
	[SELECTION_INELIGIBILITY_REASON.NOT_APPROVED]: 'Bookings.SelectToPay.Ineligible.NotApproved',
	[SELECTION_INELIGIBILITY_REASON.NO_PAYMENT]: 'Bookings.SelectToPay.Ineligible.NoPayment',
	[SELECTION_INELIGIBILITY_REASON.NOT_PAY_LATER_OR_ACCOUNT]: 'Bookings.SelectToPay.Ineligible.NotPayLaterOrAccount',
	[SELECTION_INELIGIBILITY_REASON.UNSUPPORTED_CURRENCY]: 'Bookings.SelectToPay.Ineligible.UnsupportedCurrency',
};

const ELIGIBLE = { eligible: true, reason: null };

const ineligible = (reason) => ({ eligible: false, reason });

const hasLockingBankTransferRequest = (booking) =>
	LOCKING_BANK_TRANSFER_REQUEST_STATUSES.includes(booking?.bank_transfer_request?.status);

// Client mirror of the server's `evaluateAccountSettlementRow` (packages/bank-transfer). The open
// credit-line DEBIT is guaranteed by the server-filtered Account list, so it is not re-checked here.
// payment.status is ignored: Account payments are born `billed`. There is no deadline window.
const getAccountSelectionEligibility = (booking) => {
	if (booking?.status !== RESERVATION_STATUS.APPROVED) {
		return ineligible(SELECTION_INELIGIBILITY_REASON.NOT_APPROVED);
	}

	if (booking?.payment?.manually_marked_paid === true) {
		return ineligible(SELECTION_INELIGIBILITY_REASON.ALREADY_PAID);
	}

	if (!PAY_LATER_CURRENCIES.includes(booking?.payment?.currency)) {
		return ineligible(SELECTION_INELIGIBILITY_REASON.UNSUPPORTED_CURRENCY);
	}

	if (hasLockingBankTransferRequest(booking)) {
		return ineligible(SELECTION_INELIGIBILITY_REASON.OPEN_BANK_TRANSFER_REQUEST);
	}

	return ELIGIBLE;
};

export const getBookingSelectionEligibility = (profile, booking) => {
	const accountMode = isAccountBankTransferMode(profile);
	const kind = getSettlementKind(booking);

	if (accountMode) {
		if (!booking?.payment) {
			return ineligible(SELECTION_INELIGIBILITY_REASON.NO_PAYMENT);
		}

		if (!kind) {
			return ineligible(SELECTION_INELIGIBILITY_REASON.NOT_PAY_LATER_OR_ACCOUNT);
		}

		if (kind === BANK_TRANSFER_REQUEST_KIND.ACCOUNT) {
			return getAccountSelectionEligibility(booking);
		}
	}

	if (booking?.payment?.payment_type !== PAYMENT_TYPE.PAY_LATER) {
		return ineligible(SELECTION_INELIGIBILITY_REASON.NOT_PAY_LATER);
	}

	if (booking?.payment?.status === PAYMENT_STATUS.BILLED) {
		return ineligible(SELECTION_INELIGIBILITY_REASON.ALREADY_PAID);
	}

	if (hasLockingBankTransferRequest(booking)) {
		return ineligible(SELECTION_INELIGIBILITY_REASON.OPEN_BANK_TRANSFER_REQUEST);
	}

	if (!isBankTransferAvailable(profile, booking)) {
		return ineligible(SELECTION_INELIGIBILITY_REASON.PAST_DEADLINE);
	}

	return ELIGIBLE;
};

export const getSelectableBookings = (profile, bookings) =>
	(bookings || []).filter((booking) => getBookingSelectionEligibility(profile, booking).eligible);

export const getEligibilityTooltipKey = (reason) => ELIGIBILITY_TOOLTIP_TRANSLATION_KEY[reason] || '';

export const getBookingBankTransferDeadline = (booking) => getBankTransferDeadline(booking);
